import { createAudioEl } from '@src/utils/audio';
import Janus, { JanusJS } from 'janus-gateway';

export type ConnectOpts = {
  onMessage?: (msg: JanusJS.Message) => void;
  onRemoteTrack?: (track: MediaStreamTrack, on: boolean) => void;
};

export default class CallMediaBridge {
  private handle: JanusJS.PluginHandle | null = null;

  // The attach in flight, so overlapping connects queue instead of each attaching a
  // handle and the first one being overwritten undetached.
  private connecting: Promise<void> | null = null;

  async connect(session: Janus, opts: ConnectOpts = {}) {
    if (this.connecting) await this.connecting.catch(() => undefined);
    // One bridge, one leg: a handle still attached here belongs to an earlier attempt.
    // Overwriting it without a detach left that leg joined to its room, its
    // PeerConnection open, until the whole Janus session went away.
    if (this.handle) this.disconnect();

    this.connecting = new Promise<void>((resolve, reject) => {
      // Each callback is bound to the handle it was attached with: a late event from a
      // leg this bridge already replaced (janus.js keeps delivering them until the
      // detach is acknowledged) must not reach the current leg.
      let own: JanusJS.PluginHandle | null = null;
      const current = () => (own && own === this.handle ? own : null);
      try {
        const onMessage = (msg: JanusJS.Message, jsep?: JanusJS.JSEP) => {
          const handle = current();
          if (!handle) return;
          if (jsep) handle.handleRemoteJsep({ jsep });

          // if (msg?.audiobridge === 'joined') resolve(this.handle);
          // if (msg?.audiobridge === 'joined') this.generateSdpOffer();

          opts.onMessage?.(msg);
        };

        const onRemoteTrack = (
          track: MediaStreamTrack,
          _: string,
          on: boolean
        ) => {
          if (!current() || !on || track.kind !== 'audio') return;

          this.createRemoteTrack(track);
          opts.onRemoteTrack?.(track, on);
        };

        session.attach({
          plugin: 'janus.plugin.audiobridge',
          success: _handle => {
            own = _handle;
            this.handle = _handle;
            resolve();
          },
          onmessage: onMessage,
          onremotetrack: onRemoteTrack,
          error: err => reject(err),
        });
      } catch (e) {
        reject(e);
      }
    });

    try {
      await this.connecting;
    } finally {
      this.connecting = null;
    }

    return this.handle;
  }

  send(message: JanusJS.Message, jsep?: JanusJS.JSEP) {
    this.handle?.send({ message, jsep } as JanusJS.PluginMessage);
  }

  async startAudio(stream: MediaStream, opts?: { muted: boolean }) {
    const track = stream.getAudioTracks()[0];
    if (!track) return Promise.reject();

    return new Promise((resolve, reject) => {
      const { muted = false } = opts || {};
      this.handle?.createOffer({
        tracks: [{ type: 'audio', capture: track }],
        trickle: true,
        success: (jsepOffer: JanusJS.JSEP) => {
          this.send({ request: 'configure', muted }, jsepOffer);
          resolve(stream);
        },
        error: reject,
      });
    });
  }

  disconnect() {
    try {
      this.destroyRemoteTrack();
      this.handle?.detach?.();
    } finally {
      this.handle = null;
    }
  }

  private createRemoteTrack(track: MediaStreamTrack) {
    const audioEl = createAudioEl('REMOTE_AUDIO'); //this.getRemoteAudioEl();
    let mediaStream = audioEl.srcObject as MediaStream | null;
    if (!mediaStream) {
      mediaStream = new MediaStream();
      audioEl.srcObject = mediaStream;
    }

    if (!mediaStream.getAudioTracks().some(t => t.id === track.id))
      mediaStream.addTrack(track);

    audioEl.muted = false;
    audioEl.volume = 1.0;
    audioEl.play().catch();
  }

  private destroyRemoteTrack() {
    const audioEl = document.getElementById('REMOTE_AUDIO') as HTMLAudioElement;
    if (!audioEl) return;

    const mediaStream = audioEl.srcObject as MediaStream | null;
    if (mediaStream) {
      mediaStream.getTracks().forEach(t => {
        mediaStream?.removeTrack(t);
      });
    }

    audioEl?.pause();
    audioEl.srcObject = null;
    audioEl.load();
  }
}
